import { InputColumnConfig } from "../../components/common/table_input/tableInput";
import { TableColumnConfig } from "../../components/sql_table/tableMain";
import { generateMonthYearOptions, carriersMatch } from "../../helper_functions";
import { SummaryConfig } from "../../components/sql_table/table_summary/tableSummary";

// Set to 0 (or less) to disable time-window filtering and search entire month.
const POTENTIAL_DUPLICATE_HOUR_RANGE = 3;

// Buckets rows by a time window (bucket width == hour range) so the potential-duplicate
// check below only scans a handful of nearby candidates per row instead of the whole
// table (O(n) instead of O(n^2)). Cached per `allRows` array reference via WeakMap — a
// fresh array (new data) naturally invalidates the cache, and the WeakMap doesn't block
// GC of stale entries. Ineligible candidates (blank ticket, Rejected, unparseable date)
// are filtered out once here rather than on every per-row comparison.
const duplicateTicketBucketCache = new WeakMap<any[], Map<string, any[]>>();

const getDuplicateTicketBuckets = (allRows: any[], bucketSizeMs: number): Map<string, any[]> => {
    const cached = duplicateTicketBucketCache.get(allRows);
    if (cached) return cached;

    const buckets = new Map<string, any[]>();
    for (const candidate of allRows) {
        if (!candidate || candidate.status === 'Rejected') continue;
        if (!String(candidate.ticket_number ?? '').trim()) continue;

        const candidateDate = new Date(candidate.load_time ? `${candidate.load_date}T${candidate.load_time}` : candidate.load_date);
        if (Number.isNaN(candidateDate.getTime())) continue;

        const bucketKey = bucketSizeMs > 0 ? String(Math.floor(candidateDate.getTime() / bucketSizeMs)) : 'all';
        if (!buckets.has(bucketKey)) buckets.set(bucketKey, []);
        buckets.get(bucketKey)!.push(candidate);
    }

    duplicateTicketBucketCache.set(allRows, buckets);
    return buckets;
};

interface ColumnConfig {
    tableName: string;
    columns: TableColumnConfig[];
    sortOrder?: Record<string, 'asc' | 'desc'>;
    warningRules?: any[];
    tableSummary?: SummaryConfig[];
    enableSettings?: boolean;
    showLevel: number;
    rowColor?: Array<{condition: (rowData: any) => boolean; color: string}>;
}

export const getColConfigs = (
    activeTable: string,
    producerOptions: any[],
    leaseOptions: any[],
    destinationOptions: any[],
): Record<string, ColumnConfig> => ({
    
    crude_main: {
        tableName: 'crude_main',
        showLevel: 2,
        rowColor: [
            {condition: (rowData: any) => rowData.status === 'Rejected', color: 'var(--color-red-light)'},
        ],
        sortOrder: { 
            'load_date': 'desc',
            'producer_name': 'asc',
        },
        enableSettings: false,
        warningRules:[
            {
                key: 'crude_main_wrong_lease_name',
                type: 'warning',
                colName: 'lease',
                description: 'Wrong lease name',
                condition: (rowData: any) => {
                    if (rowData.lease_whitelist == 0  || rowData.lease_whitelist == null) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
            {
                key: 'crude_main_wrong_producer_name',
                type: 'warning',
                colName: 'producer',
                description: 'Wrong producer name',
                condition: (rowData: any) => {
                    if (rowData.producer_whitelist == 0  || rowData.producer_whitelist == null) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
            {
                key: 'crude_main_ticket_not_assigned_to_load',
                type: 'alert',
                colName: 'rid',
                description: 'Ticket is not Assigned to a RID',
                condition: (rowData: any) => {
                    if (rowData.rid == ''  || rowData.rid == null) return true;


                    return false;
                },
            },
            {
                key: 'crude_main_reject_load_contains_volume',
                type: 'alert',
                colName: 'status',
                description: 'Reject Load Contains Volume',
                condition: (rowData: any) => {
                    if (rowData.status == 'Rejected') {
                        if(rowData.gross_barrels > 0 || rowData.net_barrels > 0) return true;
                    }

                    return false;
                },
            },
            {
                key: 'crude_main_volume_too_high',
                type: 'alert',
                colName: 'gross_barrels',
                description: 'Volume too high',
                condition: (rowData: any) => {
                    switch (rowData.producer){
                        case 'Steel Reef':
                            if (rowData.gross_barrels > 250 || rowData.net_barrels > 250) {
                                return true;
                            }else{
                                return false;
                            }
                        default:
                            if (rowData.gross_barrels > 220 || rowData.net_barrels > 220) {
                                return true;
                            }else{
                                return false;
                            }                   
                    }
                   
                },
            },
            {
                key: 'crude_main_pickup_delivery_gross_discrepancy',
                type:'warning',
                colName: 'gross_barrels_difference',
                description: 'Pickup/Delivery Gross Barrels Possible Discrepancy',
                condition: (rowData: any) => {
                    if (rowData.gross_barrels_difference === null || rowData.gross_barrels_difference !=0) return true;
                    return false;
                }
            }
        ],
        tableSummary: [
            { colName: 'net_barrels', operation: 'sum', label: 'Net Barrels' },
            { colName: 'total_trucking', operation: 'sum', label: 'Total Trucking', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'fuel_surcharge', operation: 'sum', label: 'Fuel Surcharge', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            // { colName: 'incidentals', operation: 'sum', label: 'Incidentals', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'total_purchase', operation: 'sum', label: 'Total Purchase', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'total_sale', operation: 'sum', label: 'Total Sale', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'net_profit', operation: 'sum', label: 'Net Profit', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
        ],
        columns: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'ticket_number',
                colHeader: 'Ticket Number',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'status',
                colHeader: 'Status',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'load_date',
                colHeader: 'Load Date',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'producer',
                colHeader: 'Producer',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'entity_name',
                colHeader: 'Entity',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'lease',
                colHeader: 'Lease',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'gross_barrels',
                colHeader: 'Gross Barrels',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'net_barrels',
                colHeader: 'Net Barrels',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'bsw',
                colHeader: 'Pickup BSW',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'corrected_gravity',
                colHeader: 'Pickup Corrected Gravity',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'carrier_name',
                colHeader: 'Carrier',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'driver',
                colHeader: 'Driver',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'total_trucking',
                colHeader: 'Total Trucking',
                colType: 'calculated',
                isVisible: (userData) => {
                    return userData?.hasAccAccess === true  || userData?.role ==='schedule_admin'},
                calculationConfig: {
                    formula: 'base_trucking + incidentals + trucking_fees',
                    labels: {
                        base_trucking: 'Base Trucking',
                        incidentals: 'Incidentals',
                        trucking_fees: 'Trucking Fees',
                    },
                    valueType: 'accounting',
                },
            },
            // {
            //     colName: 'trucking_net',
            //     colHeader: 'Base Trucking',
            //     colType: 'accounting',
            //     editable: false,
            //     isVisible: (userData) => userData?.hasAccAccess === true,
            // },
            // {
            //     colName: 'incidentals',
            //     colHeader: 'Trucking Incidentals',
            //     colType: 'accounting',
            //     editable: false,
            //     isVisible: (userData) => userData?.hasAccAccess === true,
            // },
            {
                colName: 'total_purchase',
                colHeader: 'Total Purchase',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'total_sale',
                colHeader: 'Total Sale',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'net_profit',
                colHeader: 'Net Profit',
                colType: 'accounting',
                redNegative: true,
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true || userData?.role ==='schedule_admin',
                conditionalDisplay: (value, rowData, userData) => {
                    // If user doesn't have accounting access, only show positive/negative
                    if (!userData?.hasAccAccess) {
                        
                        if (value === null || value === undefined || value === 0 || value === "") return 'No Value';
                        return value > 0 ? 'Positive' : value;
                    }
                    return value; // Show actual value for accounting users
                },
            },
            {
                colName: 'margin_per_barrel',
                colHeader: 'Margin p/bbl',
                colType: 'accounting',
                redNegative: true,
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true || userData?.role ==='schedule_admin',
                conditionalDisplay: (value, rowData, userData) => {
                    // If user doesn't have accounting access, only show positive/negative
                    if (!userData?.hasAccAccess) {
                        
                        if (value === null || value === undefined || value === 0 || value === "") return 'No Value';
                        return value > 0 ? 'Positive' : value;
                    }
                    return value; // Show actual value for accounting users
                },
            },
            {
                colName: 'destination',
                colHeader: 'Destination',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'approved_mileage',
                colHeader: 'Approved Mileage',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'approved_rate',
                colHeader: 'Approved Rate',
                colType: 'accounting',
                editable: false,
            },
        ]
    },
    crude_main_pickup: {
        tableName: 'crude_main_pickup',
        showLevel: 1,
        rowColor: [
            {condition: (rowData: any) => rowData.status === 'Rejected', color: 'var(--color-red-light)'},
        ],
        sortOrder: { 
            'load_date': 'desc',
            'producer_name': 'asc',
        },
        enableSettings: false,
        warningRules:[
            {
                key: 'crude_main_pickup_wrong_lease_name',
                type: 'warning',
                colName: 'lease',
                description: 'Wrong lease name',
                condition: (rowData: any) => {
                    if (rowData.lease_whitelist == 0  || rowData.lease_whitelist == null) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
            {
                key: 'crude_main_pickup_wrong_producer_name',
                type: 'warning',
                colName: 'producer',
                description: 'Wrong producer name',
                condition: (rowData: any) => {
                    if (rowData.producer_whitelist == 0  || rowData.producer_whitelist == null) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
            {
                key: 'crude_main_pickup_ticket_not_assigned_to_load',
                type: 'alert',
                colName: 'rid',
                description: 'Ticket is not Assigned to a RID',
                condition: (rowData: any) => {
                    if (rowData.rid == ''  || rowData.rid == null) return true;


                    return false;
                },
            },
            {
                key: 'crude_main_pickup_load_date_month_year_mismatch',
                type: 'warning',
                colName: 'month_year_display',
                description: 'Load Date Does Not Match Month/Year',
                condition: (rowData: any) => {
                    if (rowData.load_date && rowData.month_year_display) {
                        // Parse date as local date (YYYY-MM-DD format)
                        const [year, month, day] = rowData.load_date.split('-').map(Number);
                        let loadMonth = month; // Already 1-indexed
                        let loadYear = year;
                        
                        // Special case: First day of month with load_time before 7 AM can be previous month
                        if (day === 1 && rowData.load_time) {
                            // Parse load_time (assuming HH:MM:SS or HH:MM format)
                            const timeParts = rowData.load_time.split(':');
                            const hours = parseInt(timeParts[0]);
                            
                            if (hours < 7) {
                                // This load can be assigned to previous month
                                loadMonth = loadMonth - 1;
                                if (loadMonth === 0) {
                                    loadMonth = 12;
                                    loadYear = loadYear - 1;
                                }
                            }
                        }
                        
                        // Parse month_year_display which is in "MONTH YYYY" format (e.g., "March 2026")
                        const parts = rowData.month_year_display.trim().split(' ');
                        if (parts.length >= 2) {
                            const monthPart = parts[0];
                            const yearPart = parseInt(parts[parts.length - 1]);
                            
                            // Convert month name to number if it's a string
                            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                              'July', 'August', 'September', 'October', 'November', 'December'];
                            let monthNum = parseInt(monthPart);
                            
                            // If monthPart is not a number, try to find it in month names
                            if (isNaN(monthNum)) {
                                monthNum = monthNames.findIndex(name => name.toLowerCase() === monthPart.toLowerCase()) + 1;
                            }
                            
                            // For first day before 7 AM, allow either current or previous month
                            if (day === 1 && rowData.load_time) {
                                const timeParts = rowData.load_time.split(':');
                                const hours = parseInt(timeParts[0]);
                                
                                if (hours < 7) {
                                    // Calculate what the current month would be (without adjustment)
                                    const actualMonth = month;
                                    const actualYear = year;
                                    
                                    // Calculate previous month
                                    let previousMonth = month - 1;
                                    let previousYear = year;
                                    if (previousMonth === 0) {
                                        previousMonth = 12;
                                        previousYear = year - 1;
                                    }
                                    
                                    // Allow match with previous month, but NOT current month
                                    if ((monthNum === previousMonth && yearPart === previousYear)) {
                                        return false; // No warning - previous month is valid
                                    }
                                    if (monthNum === actualMonth && yearPart === actualYear) {
                                        return true; // Warning - current month is NOT valid for pre-6am loads
                                    }
                                    return true; // Warning - no match
                                }
                            }
                            
                            // Compare month and year (normal case)
                            if (loadMonth !== monthNum || loadYear !== yearPart) {
                                return true;
                            }
                        }
                    }
                    return false;
                },
            },
            {
                key: 'crude_main_pickup_potential_duplicate_ticket',
                type: 'warning',
                colName: 'ticket_number',
                description: 'Potential duplicate Ticket',
                condition: (rowData: any, allRows: any[] = []) => {
                    //exclude leases where measurements are provided to us
                    if(rowData.lease == 'Chisholm Trail Stabilizer' || rowData.lease == 'Stateline Plant') return false;

                    const toNumber = (value: any): number | null => {
                        const parsed = Number(value);
                        return Number.isFinite(parsed) ? parsed : null;
                    };

                    const isSameMeasurement = (left: number, right: number): boolean => {
                        return Math.abs(left - right) < 0.0001;
                    };

                    // load_date is a bare YYYY-MM-DD; combine with load_time for the hour-range check.
                    const toDateTime = (date: any, time: any): Date =>
                        new Date(time ? `${date}T${time}` : date);

                    const rowDate = toDateTime(rowData.load_date, rowData.load_time);
                    if (!(rowDate instanceof Date) || Number.isNaN(rowDate.getTime())) return false;

                    const rowCarrier = rowData.carrier_name;
                    const rowDriver = rowData.driver;
                    const rowTemp = toNumber(rowData.observed_temperature);
                    const rowGross = toNumber(rowData.gross_barrels);
                    const rowNet = toNumber(rowData.net_barrels);
                    const rowBsw = toNumber(rowData.bsw);

                    if (!rowCarrier || rowTemp == null || rowGross == null || rowNet == null || rowBsw == null) {
                        return false;
                    }
                    
                    // Blank ticket numbers can't be meaningfully compared for duplication.
                    if (!String(rowData.ticket_number ?? '').trim()) return false;

                    // A rejected ticket doesn't need duplicate flagging, matching the same
                    // exclusion already applied to candidates below.
                    if (rowData.status === 'Rejected') return false;

                    const useHourRangeFilter = POTENTIAL_DUPLICATE_HOUR_RANGE > 0;
                    const hourRangeMs = POTENTIAL_DUPLICATE_HOUR_RANGE * 60 * 60 * 1000;

                    // Only scan candidates near this row in time instead of the whole table.
                    // Buckets are pre-filtered (Rejected/blank-ticket/invalid-date) already,
                    // and are exactly the width of the hour range, so a match within range
                    // can only ever land in the row's own bucket or an immediate neighbor.
                    const buckets = getDuplicateTicketBuckets(allRows, useHourRangeFilter ? hourRangeMs : 0);
                    const candidates = useHourRangeFilter
                        ? (() => {
                            const rowBucket = Math.floor(rowDate.getTime() / hourRangeMs);
                            return [
                                ...(buckets.get(String(rowBucket - 1)) ?? []),
                                ...(buckets.get(String(rowBucket)) ?? []),
                                ...(buckets.get(String(rowBucket + 1)) ?? []),
                            ];
                        })()
                        : (buckets.get('all') ?? []);

                    return candidates.some((candidate: any) => {
                        if (!candidate || candidate.id === rowData.id) return false;
                        if (!carriersMatch(rowCarrier, candidate.carrier_name)) return false;
                        if (!carriersMatch(rowDriver, candidate.driver)) return false;

                        if (useHourRangeFilter) {
                            const candidateDate = toDateTime(candidate.load_date, candidate.load_time);
                            const withinHourRange = Math.abs(candidateDate.getTime() - rowDate.getTime()) <= hourRangeMs;
                            if (!withinHourRange) return false;
                        }

                        const candidateTemp = toNumber(candidate.observed_temperature);
                        const candidateGross = toNumber(candidate.gross_barrels);
                        const candidateNet = toNumber(candidate.net_barrels);
                        const candidateBsw = toNumber(candidate.bsw);

                        if (
                            candidateTemp == null ||
                            candidateGross == null ||
                            candidateNet == null ||
                            candidateBsw == null
                        ) {
                            return false;
                        }

                        return (
                            isSameMeasurement(rowTemp, candidateTemp) &&
                            isSameMeasurement(rowGross, candidateGross) &&
                            isSameMeasurement(rowNet, candidateNet) &&
                            isSameMeasurement(rowBsw, candidateBsw)
                        );
                    });
                },
                // Groups rows that matched each other under `condition` so the filtered
                // list can be sorted with duplicates adjacent instead of in table order.
                // Must mirror every field `condition` actually matches on (carrier, driver,
                // measurements, and — when the hour window is enabled — roughly when the
                // load happened) or unrelated matches (e.g. same carrier+measurements but a
                // different driver, or the same combo recurring weeks apart) end up sorted
                // into the same group.
                // Known limitation: `condition` matches carrier/driver with fuzzy substring
                // matching (`carriersMatch`), but this key uses exact normalized-string
                // equality for both — a real match with slightly different driver/carrier
                // spelling can still sort into separate groups. A fully correct fix would
                // need connected-components clustering across the dataset, which is out of
                // scope for a per-row partition key; accepted as an approximation.
                groupKey: (rowData: any): string | null => {
                    const toNumber = (value: any): number | null => {
                        const parsed = Number(value);
                        return Number.isFinite(parsed) ? parsed : null;
                    };

                    const carrier = String(rowData.carrier_name ?? '').trim().toLowerCase();
                    const driver = String(rowData.driver ?? '').trim().toLowerCase();
                    const temp = toNumber(rowData.observed_temperature);
                    const gross = toNumber(rowData.gross_barrels);
                    const net = toNumber(rowData.net_barrels);
                    const bsw = toNumber(rowData.bsw);

                    if (!carrier || !driver || temp == null || gross == null || net == null || bsw == null) {
                        return null;
                    }

                    // Coarse date bucket, same width as the duplicate-check's own bucketing —
                    // an approximation (a true match can still straddle a bucket boundary and
                    // land in two different groups), but far better than ignoring time entirely.
                    let dateBucket = '';
                    if (POTENTIAL_DUPLICATE_HOUR_RANGE > 0) {
                        const bucketSizeMs = POTENTIAL_DUPLICATE_HOUR_RANGE * 60 * 60 * 1000;
                        const rowDate = new Date(rowData.load_time ? `${rowData.load_date}T${rowData.load_time}` : rowData.load_date);
                        if (!Number.isNaN(rowDate.getTime())) {
                            dateBucket = String(Math.floor(rowDate.getTime() / bucketSizeMs));
                        }
                    }

                    return [carrier, driver, dateBucket, temp.toFixed(4), gross.toFixed(4), net.toFixed(4), bsw.toFixed(4)].join('|');
                },
            },
        ],
        tableSummary: [
            { colName: 'gross_barrels', operation: 'sum', label: 'Gross Barrels' },
            { colName: 'net_barrels', operation: 'sum', label: 'Net Barrels' },
            { colName: 'corrected_gravity', operation: 'avg', label: 'Corrected Gravity' },
            { colName: 'bsw', operation: 'avg', label: 'BS&W' },
            { colName: 'observed_temperature', operation: 'avg', label: 'Observed Temperature' },
            { colName: 'total_purchase', operation: 'sum', label: 'Total Purchase', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
        ],
        columns: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'ticket_number',
                colHeader: 'Ticket Number',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'status',
                colHeader: 'Status',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'carrier_name',
                colHeader: 'Carrier',
                colType: 'text',
                editable: false,
            },
            {   
                colName: 'driver',
                colHeader: 'Driver',
                colType: 'text',
                editable: false,
                conditionalDisplay: (value, rowData, userData) => {
                    if (value) {
                        let name: string = value.trim();
                        // "LAST, FIRST" → "FIRST LAST"
                        if (name.includes(',')) {
                            const parts = name.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
                            name = [...parts.slice(1), parts[0]].join(' ').trim();
                        }
                        // Convert all-uppercase or all-lowercase names to title case
                        if (name === name.toUpperCase() || name === name.toLowerCase()) {
                            name = name.toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
                        }
                        return name;
                    } else {
                        return "No Name Provided";
                    }
                },
            },
            {
                colName: 'load_date',
                colHeader: 'Load Date',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'load_time',
                colHeader: 'Load Time',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'month_year_display',
                colHeader: 'Month Year',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: generateMonthYearOptions(),
                editable: true,
                cellColor: [
                    { condition: (rowData: any) => rowData.month_year_mo_pickup === 1, color: 'var(--color-highlight)' }
                ],
            },
            {
                colName: 'producer',
                colHeader: 'Producer',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'entity_name',
                colHeader: 'Entity',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'lease',
                colHeader: 'Lease',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'group_split',
                colHeader: 'Field',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'tank',
                colHeader: 'Tank',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'gross_barrels',
                colHeader: 'Gross Barrels',
                colType: 'number',
                editable: true,
                subTable: 'main_table',
            },
            {
                colName: 'net_barrels',
                colHeader: 'Net Barrels',
                colType: 'number',
                editable: true,
                subTable: 'main_table',
            },
            {
                colName: 'observed_gravity',
                colHeader: 'Observed Gravity',
                colType: 'number',
                editable: true,
                subTable: 'main_table',
            },
            {
                colName: 'observed_temperature',
                colHeader: 'Observed Temperature',
                colType: 'number',
                editable: true,
                subTable: 'main_table',
            },
            {
                colName: 'corrected_gravity',
                colHeader: 'Corrected Gravity',
                colType: 'number',
                editable: true,
                subTable: 'main_table',
            },
            {
                colName: 'bsw',
                colHeader: 'BS&W',
                colType: 'number',
                editable: true,
                subTable: 'main_table',
            },
            {
                colName: 'location_deduct',
                colHeader: 'Location Deduct',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'purchase_bsw_deduct',
                colHeader: 'Purchase BSW Deduct',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'purchase_deduct',
                colHeader: 'Purchase Deduct',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            
            {
                colName: 'purchase_price',
                colHeader: 'Purchase Price',
                colType: 'accounting',
                decimals: 4,
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'total_purchase',
                colHeader: 'Total Purchase',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
        ]
    },
    crude_main_delivery: {
        tableName: 'crude_main_delivery',
        showLevel: 1,
        rowColor: [
            {condition: (rowData: any) => rowData.status === 'Rejected', color: 'var(--color-red-light)'},
        ],
        sortOrder: { 
            'load_date': 'desc',
            'producer_name': 'asc',
        },
        tableSummary: [
            { colName: 'gross_barrels', operation: 'sum', label: 'Gross Barrels' },
            { colName: 'net_barrels', operation: 'sum', label: 'Net Barrels' },
            { colName: 'corrected_gravity', operation: 'avg', label: 'Corrected Gravity' },
            { colName: 'bsw', operation: 'avg', label: 'BS&W' },
            { colName: 'total_sale', operation: 'sum', label: 'Total Sale', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
        ],
        enableSettings: true,
        warningRules:[
            {
                key: 'crude_main_delivery_wrong_station_name',
                type: 'warning',
                colName: 'destination',
                description: 'Wrong station name',
                condition: (rowData: any) => {
                    if (rowData.destination_whitelist == 0  || rowData.destination_whitelist == null) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
        ],
        columns: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'ticket_number',
                colHeader: 'Ticket Number',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'status',
                colHeader: 'Status',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'unload_date',
                colHeader: 'Unload Date',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'unload_time',
                colHeader: 'Unload Time',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'month_year_display',
                colHeader: 'Month Year',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: generateMonthYearOptions(),
                editable: true,
                cellColor: [
                    { condition: (rowData: any) => rowData.month_year_mo_delivery === 1, color: 'var(--color-highlight)' }
                ],
            },
            {
                colName: 'producer',
                colHeader: 'Producer',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'entity_name',
                colHeader: 'Entity',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'lease',
                colHeader: 'Lease',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'product_type',
                colHeader: 'Product Type',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'market',
                colHeader: 'Market',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'destination',
                colHeader: 'Destination',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'station_owner',
                colHeader: 'Station Owner',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'offload_lane',
                colHeader: 'Offload Lane',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'carrier_name',
                colHeader: 'Carrier',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'gross_barrels',
                colHeader: 'Gross Barrels',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'net_barrels',
                colHeader: 'Net Offload',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'corrected_gravity',
                colHeader: 'Corrected Gravity',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'bsw',
                colHeader: 'BS&W',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'sale_location_deduct',
                colHeader: 'Sale Location Deduct',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'sale_bsw_deduct',
                colHeader: ' Sale BS&W Deduct',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'sale_deduct',
                colHeader: 'Sale Deduct',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'sale_price',
                colHeader: 'Sale Price',
                colType: 'accounting',
                decimals: 4,
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'station_charge',
                colHeader: 'Station Charge',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'total_sale',
                colHeader: 'Total Sale',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
            {
                colName: 'market_exposure',
                colHeader: 'Market Exposure',
                colType: 'accounting',
                editable: false,
                isVisible: (userData) => userData?.hasAccAccess === true,
            },
        ]
    },

    // ── NGL Ticket Data — live, read-only view computed by fetch_ngl_ticket_data,
    // reusing the same pricing assumptions as the emailed NGL MTD report. Temporary/
    // experimental per team decision — all columns are computed or passthrough, so
    // nothing here is editable; fix underlying data on the NGL Schedule table instead.
    ngl_ticket_data: {
        tableName: 'ngl_ticket_data',
        showLevel: 2,
        sortOrder: { loadDate: 'desc' },
        rowColor: [
            { condition: (rowData: any) => rowData['Destination Configured'] === false, color: '#FDC97D' },
        ],
        tableSummary: [
            { colName: 'loaded_gross_volume', operation: 'sum', label: 'Loaded Gross Volume' },
            { colName: 'unloaded_net_volume', operation: 'sum', label: 'Unloaded Net Volume' },
            { colName: 'Purchase Total ($)', operation: 'sum', label: 'Total Purchase', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Market Sale Total ($)', operation: 'sum', label: 'Total Sale', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Producer Demurrage ($)', operation: 'sum', label: 'Total Pickup Demurrage', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Market Demurrage ($)', operation: 'sum', label: 'Total Offload Demurrage', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Net Margin ($)', operation: 'sum', label: 'Total Net Margin', format: 'currency', isVisible: (userData) => userData?.hasAccAccess === true },
        ],
        columns: [
            { colName: 'ticket_number', colHeader: 'Ticket Number', colType: 'text', editable: false },
            { colName: 'loadDate', colHeader: 'Load Date', colType: 'text', editable: false },
            { colName: 'producer_name', colHeader: 'Producer', colType: 'text', editable: false },
            { colName: 'lease_name', colHeader: 'Lease', colType: 'text', editable: false },
            { colName: 'Stream', colHeader: 'Stream', colType: 'text', editable: false },
            { colName: 'Destination', colHeader: 'Destination', colType: 'text', editable: false },
            { colName: 'carrier_name', colHeader: 'Carrier', colType: 'text', editable: false },
            { colName: 'status', colHeader: 'Status', colType: 'text', editable: false },
            { colName: 'driver_notes', colHeader: 'Driver Notes', colType: 'text', editable: false },
            { colName: 'loaded_gross_volume', colHeader: 'Loaded Gross Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'unloaded_net_volume', colHeader: 'Unloaded Net Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'C1 Volume (gal)', colHeader: 'C1 Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'C2 Volume (gal)', colHeader: 'C2 Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'C3 Volume (gal)', colHeader: 'C3 Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'IC4 Volume (gal)', colHeader: 'IC4 Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'NC4 Volume (gal)', colHeader: 'NC4 Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'C5+ Volume (gal)', colHeader: 'C5+ Volume (gal)', colType: 'number', decimals: 1, editable: false },
            { colName: 'Purchase Total ($)', colHeader: 'Purchase Total', colType: 'accounting', editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Market Sale Total ($)', colHeader: 'Market Sale Total', colType: 'accounting', editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Transport ($)', colHeader: 'Transport', colType: 'accounting', editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Market Demurrage ($)', colHeader: 'Market Demurrage', colType: 'accounting', editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Producer Demurrage ($)', colHeader: 'Producer Demurrage', colType: 'accounting', editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Producer FSC ($)', colHeader: 'Producer FSC', colType: 'accounting', editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Net Margin ($)', colHeader: 'Net Margin', colType: 'accounting', redNegative: true, editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Margin ($/gal)', colHeader: 'Margin ($/gal)', colType: 'accounting', decimals: 3, redNegative: true, editable: false, isVisible: (userData) => userData?.hasAccAccess === true },
            { colName: 'Composition Source', colHeader: 'Composition Source', colType: 'text', editable: false },
            { colName: 'Volume Source', colHeader: 'Volume Source', colType: 'text', editable: false },
            { colName: 'Off Spec', colHeader: 'Off Spec', colType: 'boolean', editable: false },
        ]
    },

    // ── Vac Tickets — Phase 6 financial tab ──────────────────────────────────
    vac_tickets: {
        tableName: 'vac_tickets',
        showLevel: 2,
        sortOrder: { oi_service_date: 'desc' },
        rowColor: [
            {
                condition: (r: any) => r.oi_status === 'DISPUTED',
                color: '#fff3cd',
            },
            {
                condition: (r: any) =>
                    r.oi_status === 'APPROVED' && r.oi_invoiced_status === 'FULLY_INVOICED',
                color: 'var(--color-green-complete)',
            },
        ],
        warningRules: [
            {
                key: 'vac_tickets_disputed_status',
                type: 'alert',
                colName: 'oi_status',
                description: 'Receipt is in DISPUTED status',
                condition: (r: any) => r.oi_status === 'DISPUTED',
            },
            {
                key: 'vac_tickets_approved_not_invoiced',
                type: 'warning',
                colName: 'oi_invoiced_status',
                description: 'Approved receipt has not been invoiced',
                condition: (r: any) =>
                    r.oi_status === 'APPROVED' && r.oi_invoiced_status === 'NOT_INVOICED',
            },
            {
                key: 'vac_tickets_no_matching_rid',
                type: 'warning',
                colName: 'rid',
                description: 'No matching schedule/RID found',
                condition: (r: any) => !r.rid,
            },
            {
                key: 'vac_tickets_disposal_no_bottoms_match',
                type: 'warning',
                colName: 'disposal_match',
                description: 'Disposal ticket has no matching bottoms record',
                condition: (r: any) =>
                    r.work_types?.includes('disposal') && !r.disposal_matched,
            },
        ],
        tableSummary: [],
        columns: [
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
                cellColor: [
                    { condition: (r: any) => !r.rid,  color: 'var(--color-red-light)' },
                    { condition: (r: any) => !!r.rid, color: 'var(--color-green-complete)' },
                ],
            },
            {
                colName: 'oi_ticket_number',
                colHeader: 'OT Receipt #',
                colType: 'text',
                editable: false,
                enableSidepanel: true,
            },
            {
                colName: 'oi_service_date',
                colHeader: 'Service Date',
                colType: 'date',
                editable: false,
            },
            {
                colName: 'oi_producer',
                colHeader: 'Producer',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'oi_lease',
                colHeader: 'Lease',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'oi_carrier',
                colHeader: 'Carrier',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'oi_driver',
                colHeader: 'Driver',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'work_types',
                colHeader: 'Work Types',
                colType: 'text',
                editable: false,
                conditionalDisplay: (value: any) => {
                    if (!Array.isArray(value) || value.length === 0) return '—';
                    const labels: Record<string, string> = {
                        standard_vac: 'Vac',
                        disposal:     'Disp',
                        freshwater:   'Fresh',
                        washout:      'Washout',
                        undetermined: '?',
                    };
                    return value.map((t: string) => labels[t] ?? t).join(' / ');
                },
            },
            
            {
                colName: 'oi_status',
                colHeader: 'OI Status',
                colType: 'text',
                editable: false,
                cellColor: [
                    { condition: (r: any) => r.oi_status === 'APPROVED',   color: '#d4edda' },
                    { condition: (r: any) => r.oi_status === 'SUBMITTED',  color: '#fff3cd' },
                    { condition: (r: any) => r.oi_status === 'DISPUTED',   color: '#f8d7da' },
                    { condition: (r: any) => r.oi_status === 'CANCELLED',  color: 'var(--color-grey-light-darker)' },
                ],
            },
            {
                colName: 'oi_invoiced_status',
                colHeader: 'Carrier Invoice Status',
                colType: 'text',
                editable: false,
                cellColor: [
                    { condition: (r: any) => r.oi_invoiced_status === 'FULLY_INVOICED',     color: '#d4edda' },
                    { condition: (r: any) => r.oi_invoiced_status === 'PARTIALLY_INVOICED', color: '#fff3cd' },
                    { condition: (r: any) => r.oi_invoiced_status === 'NOT_INVOICED',       color: '#f8d7da' },
                ],
            },
            {
                colName: 'disposal_match',
                colHeader: 'Disp. Match',
                colType: 'text',
                editable: false,
                conditionalDisplay: (_value: any, rowData: any) => {
                    if (!rowData.work_types?.includes('disposal')) return 'N/A';
                    return rowData.disposal_matched ? 'Yes' : 'No';
                },
                cellColor: [
                    {
                        condition: (r: any) =>
                            r.work_types?.includes('disposal') && r.disposal_matched,
                        color: 'var(--color-green-complete)',
                    },
                    {
                        condition: (r: any) =>
                            r.work_types?.includes('disposal') && !r.disposal_matched,
                        color: '#fff3cd',
                    },
                ],
            },
            {
                colName: 'oi_disposal_ticket',
                colHeader: 'Facility Ticket(s)',
                colType: 'text',
                editable: false,
            },

            {
                colName: 'oi_invoice_number',
                colHeader: 'Invoice #',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'rb_last_action_user',
                colHeader: 'Reviewed By',
                colType: 'text',
                editable: false,
            },
        ],
    },

    vac_accounts_payable: {
        tableName: 'vac_accounts_payable',
        showLevel: 2,
        sortOrder: { load_date: 'desc' },
        rowColor: [
            {
                condition: (r: any) => r.unmatched_disposal_tickets && r.unmatched_disposal_tickets !== '[]',
                color: 'var(--color-red-light)',
            },
            {
                condition: (r: any) => r.oi_invoiced_status === 'APPROVED',
                color: 'var(--color-green-complete)',
            },
        ],
        warningRules: [
            {
                key: 'vac_accounts_payable_disposal_ticket_not_matched',
                type: 'alert',
                colName: 'unmatched_disposal_tickets',
                description: 'Disposal ticket not matched to a bottoms record',
                condition: (r: any) => r.unmatched_disposal_tickets && r.unmatched_disposal_tickets !== '[]',
            },
        ],
        tableSummary: [
            {
                colName: 'service_cost',
                operation: 'sum',
                label: 'Service',
                format: 'currency',
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            {
                colName: 'freshwater_cost',
                operation: 'sum',
                label: 'Fresh Water',
                format: 'currency',
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            {
                colName: 'washout_cost',
                operation: 'sum',
                label: 'Washout',
                format: 'currency',
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            {
                colName: 'total_cost',
                operation: 'sum',
                label: 'Total Cost',
                format: 'currency',
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            { colName: 'disposal_volume', operation: 'sum', label: 'Disposal Vol', decimals: 2 },
        ],
        columns: [
            { colName: 'id',                colHeader: 'ID',            colType: 'text',       editable: false },
            { colName: 'rid',               colHeader: 'RID',           colType: 'text',       editable: false },
            { colName: 'oi_ticket_number',  colHeader: 'OI Ticket',     colType: 'text',       editable: false },
            { colName: 'load_date',         colHeader: 'Load Date',     colType: 'date',       editable: false },
            { colName: 'producer_name',     colHeader: 'Producer',      colType: 'text',       editable: false },
            { colName: 'lease_name',        colHeader: 'Lease',         colType: 'text',       editable: false },
            { colName: 'carrier_name',      colHeader: 'Carrier',       colType: 'text',       editable: false },
            { colName: 'oi_invoice_number', colHeader: 'Invoice #',     colType: 'text',       editable: false },
            { colName: 'oi_status',         colHeader: 'OI Status',     colType: 'text',       editable: false },
            { colName: 'oi_invoiced_status',colHeader: 'Invoice Status',colType: 'text',       editable: false },
            {
                colName: 'service_cost',
                colHeader: 'Service Cost',
                colType: 'accounting',
                decimals: 2,
                editable: false,
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            { colName: 'disposal_volume',   colHeader: 'Disposal Vol',  colType: 'number',     decimals: 2, editable: false },
            {
                colName: 'disposal_cost',
                colHeader: 'Disposal Cost',
                colType: 'accounting',
                decimals: 2,
                editable: false,
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            {
                colName: 'freshwater_cost',
                colHeader: 'Fresh Water',
                colType: 'accounting',
                decimals: 2,
                editable: false,
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            {
                colName: 'washout_cost',
                colHeader: 'Washout',
                colType: 'accounting',
                decimals: 2,
                editable: false,
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            {
                colName: 'total_cost',
                colHeader: 'Total Cost',
                colType: 'accounting',
                decimals: 2,
                editable: false,
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            { colName: 'matched_disposal_tickets',   colHeader: 'Matched FTs',   colType: 'text', editable: false },
            { colName: 'unmatched_disposal_tickets', colHeader: 'Unmatched FTs', colType: 'text', editable: false },
            { colName: 'last_updated',               colHeader: 'Last Updated',  colType: 'date', editable: false },
        ],
    },

    vac_ap_orphaned_oi: {
        tableName: 'vac_ap_orphaned_oi',
        showLevel: 2,
        sortOrder: { oi_service_date: 'desc' },
        columns: [
            { colName: 'oi_ticket_number',   colHeader: 'OI Ticket',     colType: 'text',  editable: false },
            { colName: 'oi_line_item_number',colHeader: 'Line #',        colType: 'text',  editable: false },
            { colName: 'oi_service_date',    colHeader: 'Service Date',  colType: 'date',  editable: false },
            { colName: 'ticket_type',        colHeader: 'Type',          colType: 'text',  editable: false },
            { colName: 'oi_producer',        colHeader: 'Producer',      colType: 'text',  editable: false },
            { colName: 'oi_lease',           colHeader: 'Lease',         colType: 'text',  editable: false },
            { colName: 'oi_carrier',         colHeader: 'Carrier',       colType: 'text',  editable: false },
            {
                colName: 'oi_line_total',
                colHeader: 'Line Total',
                colType: 'accounting',
                decimals: 2,
                editable: false,
                isVisible: (userData: any) => userData?.hasAccAccess === true,
            },
            { colName: 'oi_invoice_number',  colHeader: 'Invoice #',     colType: 'text',  editable: false },
            { colName: 'oi_status',          colHeader: 'OI Status',     colType: 'text',  editable: false },
            { colName: 'oi_invoiced_status', colHeader: 'Invoice Status',colType: 'text',  editable: false },
        ],
    },

    vac_ap_orphaned_disposal: {
        tableName: 'vac_ap_orphaned_disposal',
        showLevel: 2,
        sortOrder: { final_load_date: 'desc' },
        columns: [
            { colName: 'FT_Number',            colHeader: 'FT #',         colType: 'text',   editable: false },
            { colName: 'final_ticket_number',  colHeader: 'Final Ticket', colType: 'text',   editable: false },
            { colName: 'final_load_date',      colHeader: 'Load Date',    colType: 'date',   editable: false },
            { colName: 'final_carrier',        colHeader: 'Carrier',      colType: 'text',   editable: false },
            { colName: 'Facility',             colHeader: 'Facility',     colType: 'text',   editable: false },
            { colName: 'Operator',             colHeader: 'Operator',     colType: 'text',   editable: false },
            { colName: 'Net_Barrels',          colHeader: 'Net BBL',      colType: 'number', decimals: 2, editable: false },
        ],
    },
});